/**
 * The Solutions catalogue — one entry per capability we sell. It drives both the
 * Solutions dropdown in the public header and the Solutions page, so the two can
 * never drift, and adding an offering is a single entry here (extensible by
 * design, per the Phase 1 brief).
 *
 * `available: false` marks something on the roadmap rather than shipped, and
 * paints a "Soon" chip in the header dropdown. Nothing carries it today: the
 * property marketplace was the last entry marked that way and it shipped —
 * publish a vacant unit, tenants search /homes, enquiries land back in the
 * landlord's inbox. The flag stayed false for months afterwards, so the site
 * was advertising a built feature as unavailable while the header linked
 * straight to it. Worth remembering the flag is a claim about the product and
 * has to be revisited when the product changes.
 *
 * Anchors match the hrefs already used in the site footer (#rent-payments etc.),
 * so a footer link scrolls to the right section on /solutions.
 */
export interface Solution {
  id: string;
  name: string;
  /** One line for the dropdown and the section sub-heading. */
  tagline: string;
  /** A short paragraph for the Solutions page. */
  description: string;
  /** Concrete capabilities — what a landlord actually gets. */
  points: string[];
  icon: string;
  available?: boolean;
}

export const SOLUTIONS: Solution[] = [
  {
    id: 'landlord-pm',
    name: 'Landlord property management',
    tagline: 'Run your whole portfolio from one place',
    description:
      'Built for landlords who manage their own units, not for agencies with a back office. Everything a small or growing landlord needs to stay on top of rent, tenants and buildings, without a spreadsheet in sight.',
    points: [
      'One dashboard across every property you own',
      'Works on the phone in your pocket, not just a desktop',
      'No training day, a landlord understands it on first open',
    ],
    icon: '⌂',
    available: true,
  },
  {
    id: 'property-management',
    name: 'Property and unit management',
    tagline: 'Every building, every unit, always current',
    description:
      'Organise properties and the units inside them, track who is in and what is empty, and see occupancy at a glance. Search and filters find any unit in seconds, however large the portfolio grows.',
    points: [
      'Properties, units and occupancy in one structure',
      'Vacant, occupied, reserved and under maintenance at a glance',
      'Fast search and filters across the whole portfolio',
    ],
    icon: '▦',
    available: true,
  },
  {
    id: 'rent-payments',
    name: 'Rent and payments',
    tagline: 'Charged, collected and reconciled without the arithmetic',
    description:
      'Rent is generated on schedule, payments are recorded against the right charge, and part payments, advances and credit are handled correctly. Receipts and statements come out clean, so the numbers always agree.',
    points: [
      'Automatic monthly rent generation per unit',
      'Full, partial and advance payments allocated correctly',
      'Receipts, statements and a clear arrears view',
    ],
    icon: '₪',
    available: true,
  },
  {
    id: 'tenant-management',
    name: 'Tenant management',
    tagline: 'Know every tenant and every tenancy',
    description:
      'A record for each tenant, the tenancy that ties them to a unit, and the history behind both. Contact details, lease dates and balances live together, so nothing about a tenant is lost between screens.',
    points: [
      'Tenant profiles with full contact and lease detail',
      'Tenancies linking tenant to unit, with start and end dates',
      'A history you can trust when a question comes up',
    ],
    icon: '☺',
    available: true,
  },
  {
    id: 'analytics',
    name: 'Portfolio intelligence',
    tagline: 'Where the portfolio is heading, not just where it has been',
    description:
      'Occupancy, collection rate, income against expenses and arrears, presented as answers rather than raw charts, with arrears banded by age so a small gap surfaces early. Reports export to spreadsheet when someone downstream needs the detail.',
    points: [
      'Portfolio KPIs: occupancy, collection rate, net income',
      'Arrears banded by age, so the oldest debt is chased first',
      'Rent roll, income statements and every report as a spreadsheet',
    ],
    icon: '▤',
    available: true,
  },
  {
    id: 'maintenance-operations',
    name: 'Maintenance and operations',
    tagline: 'Keep buildings running and costs recorded',
    description:
      'Log maintenance, track expenses against properties, and keep the documents that matter in one place. The day-to-day operations of a portfolio, captured where the rest of the property lives.',
    points: [
      'Maintenance requests tracked to resolution',
      'Expenses recorded against the right property',
      'Documents kept alongside the unit they belong to',
    ],
    icon: '⚙',
    available: true,
  },
  {
    id: 'listing-search',
    name: 'Property listing and home search',
    tagline: 'List vacant units and let tenants find them',
    description:
      'A public marketplace where your vacant units are advertised and people looking for a home can search them. Publish straight from the unit you already hold, and enquiries come back to you inside NyumbaSuite.',
    points: [
      'Publish a vacant unit from your portfolio, with no re-typing',
      'Searchable by area, rent, type and bedrooms, with photos',
      'Enquiries flow back to you inside NyumbaSuite',
    ],
    icon: '⌕',
    available: true,
  },
  {
    id: 'tenant-portal',
    name: 'Tenant portal and assistant',
    tagline: 'Give every tenant a login of their own',
    description:
      'Tenants see their balance, charges, invoices, payments, statements, lease and documents without ringing you, raise a maintenance request and follow it, and ask an assistant of their own the questions they would otherwise ask you.',
    points: [
      'Invitation-only access, tied to the tenancy you already hold',
      'Statements, invoices, receipts and documents on demand',
      'Maintenance requests raised and tracked by the tenant',
    ],
    icon: '☖',
    available: true,
  },
  {
    id: 'leases-inspections',
    name: 'Leases and inspections',
    tagline: 'The paperwork that settles the argument later',
    description:
      'Formal lease documents with renewal reminders, and room-by-room move-in and move-out inspections the tenant signs from their own phone, so a deposit dispute is settled by the record rather than by memory.',
    points: [
      'Lease lifecycle with reminders at 90, 60 and 30 days',
      'Inspections with photos, meter readings and a tenant signature',
      'Move-out compared against move-in, into a deposit worksheet',
    ],
    icon: '✎',
    available: true,
  },
  {
    id: 'ai-assistant',
    name: 'NyumbaSuite AI',
    tagline: 'Ask your portfolio a question',
    description:
      'An assistant that answers from your own data: who is in arrears, which units are empty, which leases expire soon. It works within the same permissions you already hold, and it reads rather than writes, so it can explain your portfolio but never alter it.',
    points: [
      'Answers built from your live records, not a canned reply',
      'Limited to what your own role is allowed to see',
      'Read-only by design, so it cannot change a thing',
    ],
    icon: '✦',
    available: true,
  },
];

export function findSolution(id: string): Solution | undefined {
  return SOLUTIONS.find((s) => s.id === id);
}
